import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { ShieldCheck, Home, Bot, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export default function ProductsSection({ texts }) {
  const navigate = useNavigate();
  const products = [
    {
      icon: ShieldCheck,
      title: texts.items[0].title,
      description: texts.items[0].description,
      features: texts.items[0].features,
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-900/20 to-pink-900/20",
      hoverGradient: "hover:from-purple-600/20 hover:to-pink-600/20",
      slug: "tseg"
    },
    {
      icon: Home,
      title: texts.items[1].title,
      description: texts.items[1].description,
      features: texts.items[1].features,
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-900/20 to-cyan-900/20",
      hoverGradient: "hover:from-blue-600/20 hover:to-cyan-600/20",
      slug: "nexus-pay"
    },
    {
      icon: Bot,
      title: texts.items[2].title,
      description: texts.items[2].description,
      features: texts.items[2].features,
      gradient: "from-green-500 to-teal-500",
      bgGradient: "from-green-900/20 to-teal-900/20",
      hoverGradient: "hover:from-green-600/20 hover:to-teal-600/20",
      slug: "sentinel-ai"
    }
  ];

  return (
    //<section id="products" className="py-24 bg-gray-950/50 relative z-10">
    <section id="products" className="py-24 bg-black relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="bg-gray-800 text-blue-400 border-blue-500/20 mb-4">
            {texts.badge}
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {texts.title1}
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {texts.title2}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {texts.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 items-stretch">
          {products.map((product, index) => (
            <motion.div
              key={product.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <Card className={`h-full bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-all duration-500 group hover:transform hover:scale-105 ${product.hoverGradient} hover:bg-gray-900/70 rounded-2xl`}>
                <CardContent className="px-10 pb-10 pt-10 flex flex-col h-full">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-r ${product.bgGradient} border border-gray-700 mb-8 group-hover:border-gray-600 transition-all duration-300 mt-2`}>
                    {product.slug === 'tseg' ? (
                      <img 
                        src={require("../../assets/Logo_TSEG.png")} 
                        alt="TSEG Logo" 
                        className="w-18 h-18 object-contain"
                      />
                    ) : product.slug === 'nexus-pay' ? (
                      <span className="text-4xl">💳</span>
                    ) : product.slug === 'sentinel-ai' ? (
                      <span className="text-4xl">🤖</span>
                    ) : (
                      <product.icon className={`w-10 h-10 bg-gradient-to-r ${product.gradient} bg-clip-text text-transparent`} />
                    )}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors duration-300">
                    {product.title}
                  </h3>
                  
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {product.description}
                  </p>
                  
                  <div className="space-y-2 mb-10">
                    {product.features.map((feature) => (
                      <div key={feature} className="flex items-center space-x-2">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${product.gradient}`} />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-auto">
                    <Button 
                      className={`w-full bg-gradient-to-r ${product.gradient} hover:opacity-90 text-white font-medium transition-all duration-300 group-hover:shadow-lg group-hover:shadow-blue-500/25`}
                      onClick={() => navigate(product.slug === 'tseg' ? '/tseg' : `/products/${product.slug}`)}
                    >
                      {texts.learnMore}
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
